import { useEffect, useState, Fragment } from 'react';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import { listUsers, createUser, updateUserFull, removeUser, updateSalary, updateSyncAgent, updateContact, listDeptHeads, setDeptHead, setDeputy, resetPassword, setUserResigned, createPartner, setPartnerContacts, convertToPartner } from '../api/users';
import { confirm } from '../utils/confirm';
import { listGroups, createGroup, updateGroup, removeGroup, setGroupHead } from '../api/groups';
import { fmtVND } from '../utils/format';
import Avatar from '../components/Avatar';
import LeaveHistoryModal from '../components/LeaveHistoryModal';
import { fileToAvatarDataUrl } from '../utils/image';

// SĐT phải đúng 10 chữ số.
const isValidPhone = (p) => /^\d{10}$/.test(String(p || ''));

// Ô upload ảnh đại diện dùng chung cho form tạo/sửa.
function AvatarPicker({ name, value, onChange }) {
  async function pick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      onChange(await fileToAvatarDataUrl(file));
    } catch {
      /* ignore */
    }
  }
  return (
    <div className="row" style={{ gap: 12, alignItems: 'center' }}>
      <Avatar src={value} name={name} size={56} />
      <div>
        <label className="btn sm ghost" style={{ cursor: 'pointer' }}>
          {value ? 'Đổi ảnh' : 'Tải ảnh lên'}
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={pick} />
        </label>
        {value && (
          <button className="btn sm ghost" style={{ marginLeft: 6 }} onClick={() => onChange('')}>
            Xóa
          </button>
        )}
      </div>
    </div>
  );
}
import { useAuth } from '../context/AuthContext';

// GĐ và Cố vấn đều là admin — không gắn lương/phụ cấp, không xóa.
const isAdminRole = (r) => r === 'GD' || r === 'CV';

// Phòng ban cố định (1 nhân viên có thể kiêm nhiều phòng).
const DEPARTMENTS = ['Phòng kinh doanh', 'Phòng CSKH', 'Phòng DVKH', 'Phòng OP', 'Phòng kế toán', 'Phòng công nghệ'];
// NV kiêm nhiều phòng: phòng ĐẦU mảng = phòng CHÍNH (nơi làm việc chính, tính lương/chấm công ở đó);
// các phòng CÒN LẠI -> NV là "CTV" (cộng tác viên) của phòng đó. Tự suy ra, không cần cờ riêng.
const deptShort = (d) => String(d || '').replace(/^Phòng\s+/i, '');
const ctvDepts = (departments) => (departments || []).slice(1);

export default function StaffPage() {
  const { me } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState('');
  const [hideResigned, setHideResigned] = useState(false); // ẩn nhân viên đã nghỉ khỏi danh sách

  async function reload() {
    const [u, g] = await Promise.all([listUsers(), listGroups()]);
    setUsers(u);
    setGroups(g);
  }

  useEffect(() => {
    reload();
  }, []);

  async function toggleAgent(u) {
    try {
      await updateSyncAgent(u.id, !u.syncAgent);
      toast(!u.syncAgent ? `Đã bật máy đồng bộ cho ${u.name}` : `Đã tắt máy đồng bộ của ${u.name}`);
      reload();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function doRemoveUser(u) {
    // XÓA TÀI KHOẢN là hành động KHÔNG HOÀN TÁC -> BẮT BUỘC hỏi lại (trước đây xóa ngay, dễ bấm nhầm -> mất
    // tài khoản + lịch sử). Với nhân viên: gợi ý dùng "Đánh dấu đã nghỉ việc" (giữ lịch sử) thay vì xóa hẳn.
    const isPartner = u.role === 'DT';
    const yes = await confirm(
      isPartner
        ? `Xóa VĨNH VIỄN tài khoản đối tác "${u.name}"?\nHành động này KHÔNG THỂ hoàn tác.`
        : `Xóa VĨNH VIỄN tài khoản "${u.name}"${u.user ? ` (${u.user})` : ''}?\n\n`
          + `• KHÔNG THỂ hoàn tác — mất luôn lịch sử công/lương/tin nhắn.\n`
          + `• Nên cân nhắc "Đánh dấu đã nghỉ việc": chặn đăng nhập nhưng GIỮ nguyên lịch sử, mở lại được sau.`,
      { title: 'Xóa tài khoản', okText: 'Xóa vĩnh viễn', cancelText: 'Hủy', danger: true }
    );
    if (!yes) return;
    try {
      await removeUser(u.id);
      toast('Đã xóa tài khoản', 'warn');
      reload();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  // Đánh dấu ĐÃ NGHỈ / làm việc lại. Đã nghỉ: chặn đăng nhập, chat hiện "đã nghỉ việc";
  // KHÔNG xoá tài khoản — giữ nguyên lịch sử công/lương/tin nhắn.
  async function doToggleResigned(u) {
    const toResign = !u.resignedAt;
    const yes = await confirm(
      toResign
        ? `Đánh dấu ${u.name} ĐÃ NGHỈ VIỆC?\n- Không thể đăng nhập nữa (phiên đang mở cũng bị chặn)\n- Chat hiển thị "đã nghỉ việc"\n- Tài khoản + lịch sử vẫn giữ nguyên, có thể mở lại sau`
        : `Cho ${u.name} LÀM VIỆC TRỞ LẠI? Tài khoản sẽ đăng nhập được như bình thường.`
    );
    if (!yes) return;
    try {
      await setUserResigned(u.id, toResign);
      toast(toResign ? `${u.name} đã được đánh dấu nghỉ việc` : `${u.name} đã làm việc trở lại`);
      reload();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function doResetPassword(u) {
    const yes = await confirm(
      `Reset mật khẩu của ${u.name}?\nHệ thống sẽ tạo mật khẩu ngẫu nhiên mới và bắt nhân viên đổi mật khẩu khi đăng nhập lần tới.`
    );
    if (!yes) return;
    try {
      const r = await resetPassword(u.id);
      openModal(<ResetPasswordResult name={r.name} login={r.user} password={r.password} onClose={closeModal} />);
    } catch (err) {
      toast(err.response?.data?.message || 'Lỗi reset mật khẩu', 'danger');
    }
  }

  function openCreateUser() {
    openModal(
      <CreateUserForm
        groups={groups}
        onCancel={closeModal}
        onSubmit={async (form) => {
          try {
            await createUser(form);
            closeModal();
            toast('Đã tạo tài khoản ' + form.name);
            reload();
          } catch (err) {
            toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
          }
        }}
      />
    );
  }

  // Danh sách người có thể được đối tác chat (mọi nhân sự đang làm việc, KHÔNG gồm đối tác khác).
  const contactPool = users.filter((u) => u.role !== 'DT' && !u.resignedAt);

  function openCreatePartner() {
    openModal(
      <PartnerForm pool={contactPool} onCancel={closeModal}
        onSubmit={async (form) => {
          try { await createPartner(form); closeModal(); toast('Đã tạo tài khoản đối tác ' + form.name); reload(); }
          catch (err) { toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger'); }
        }} />
    );
  }
  function openPartnerContacts(u) {
    openModal(
      <PartnerForm pool={contactPool} editUser={u} onCancel={closeModal}
        onSubmit={async (form) => {
          try { await setPartnerContacts(u.id, form.contacts); closeModal(); toast('Đã cập nhật danh sách chat của ' + u.name); reload(); }
          catch (err) { toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger'); }
        }} />
    );
  }
  // CHUYỂN nhân viên (NV/TP) -> tài khoản đối tác: cảnh báo -> chọn danh bạ chat (tái dùng PartnerForm) -> gọi API.
  async function doConvertToPartner(u) {
    const yes = await confirm(
      `Chuyển "${u.name}"${u.user ? ` (${u.user})` : ''} thành tài khoản ĐỐI TÁC?\n\n`
      + `• ẨN TOÀN BỘ dữ liệu cũ — sau khi chuyển chỉ đăng nhập thấy phần Tin nhắn.\n`
      + `• Chỉ chat được với những người bạn chọn ở bước sau.\n`
      + `• Mất mọi quyền nhân viên (phòng ban, nhóm, lương, chấm công…).\n`
      + `• Khách đang phụ trách sẽ thành "chưa có sale care" để Giám đốc thấy.`,
      { title: 'Chuyển thành đối tác', okText: 'Chuyển thành đối tác', cancelText: 'Hủy', danger: true }
    );
    if (!yes) return;
    openModal(
      <PartnerForm pool={contactPool.filter((p) => p.id !== u.id)} editUser={u}
        title={`Chuyển "${u.name}" thành đối tác`}
        subtitle="Chọn những người mà đối tác này được phép chat. Sau khi chuyển, họ chỉ thấy Tin nhắn với các người này."
        okText="Xác nhận chuyển" onCancel={closeModal}
        onSubmit={async (form) => {
          try { await convertToPartner(u.id, form.contacts); closeModal(); toast(`Đã chuyển ${u.name} thành tài khoản đối tác`); reload(); }
          catch (err) { toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger'); }
        }} />
    );
  }

  function openManageGroups() {
    openModal(<GroupsModal onClose={closeModal} onChanged={reload} toast={toast} />);
  }

  function openDeptHeads() {
    openModal(<DeptHeadsModal onClose={closeModal} toast={toast} />);
  }

  function openDeputyHeads() {
    openModal(<DeputyHeadsModal onClose={closeModal} onChanged={reload} toast={toast} />);
  }

  function openEditSalary(u) {
    openModal(
      <EditSalaryForm
        user={u}
        onCancel={closeModal}
        onSubmit={async (payload) => {
          try {
            await updateSalary(u.id, payload);
            closeModal();
            toast('Đã cập nhật lương');
            reload();
          } catch (err) {
            toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
          }
        }}
      />
    );
  }

  function openEditContact(u) {
    openModal(
      <EditContactForm
        user={u}
        onCancel={closeModal}
        onSubmit={async (payload) => {
          try {
            await updateContact(u.id, payload);
            closeModal();
            toast('Đã cập nhật thông tin liên hệ');
            reload();
          } catch (err) {
            toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
          }
        }}
      />
    );
  }

  function openEditUser(u) {
    openModal(
      <EditUserForm
        user={u}
        groups={groups}
        onCancel={closeModal}
        onSubmit={async (payload) => {
          try {
            await updateUserFull(u.id, payload);
            closeModal();
            toast('Đã cập nhật thông tin ' + payload.name);
            reload();
          } catch (err) {
            toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
          }
        }}
      />
    );
  }

  // Trưởng phòng = TP nhóm kinh doanh HOẶC trưởng phòng ban khác (CSKH/DVKH/OP/kế toán/công nghệ)
  // — dù role='NV' nhưng được đặt làm trưởng phòng thì vẫn ngang hàng TP.
  const isHead = (u) => u.role === 'TP' || (u.deptHeadOf && u.deptHeadOf.length > 0);
  // Nhóm theo CẤP BẬC (thiết kế mới): Giám đốc/Cố vấn → Trưởng phòng → Nhân viên.
  const RANKS = [
    { key: 'admin', label: 'Giám đốc', dot: 'var(--vip)', match: (u) => u.role === 'GD' || u.role === 'CV' },
    { key: 'tp', label: 'Trưởng phòng', dot: 'var(--accent)', match: (u) => (u.role === 'GD' || u.role === 'CV') ? false : u.role !== 'DT' && isHead(u) },
    { key: 'pp', label: 'Phó phòng', dot: 'var(--warn)', match: (u) => u.role === 'NV' && u.isDeputy && !isHead(u) },
    { key: 'nv', label: 'Nhân viên', dot: 'var(--ok)', match: (u) => u.role !== 'GD' && u.role !== 'CV' && u.role !== 'DT' && !isHead(u) && !u.isDeputy },
    { key: 'dt', label: 'Đối tác', dot: 'var(--accent2)', match: (u) => u.role === 'DT' },
  ];
  const ql = q.trim().toLowerCase();
  const matchQ = (u) => !ql || [u.name, u.user, u.email, u.employeeCode, u.phone].some((v) => String(v || '').toLowerCase().includes(ql));
  // Ẩn nhân viên đã nghỉ: lọc bỏ khỏi danh sách + đếm (số liệu trên khớp với dòng đang hiện).
  const resignedCount = users.filter((u) => u.resignedAt).length;
  const visibleUsers = hideResigned ? users.filter((u) => !u.resignedAt) : users;
  const counts = RANKS.map((r) => ({ ...r, n: visibleUsers.filter(r.match).length }));
  const rankGroups = RANKS.map((r) => ({ ...r, rows: visibleUsers.filter((u) => r.match(u) && matchQ(u)) })).filter((g) => g.rows.length);

  // Nút thao tác chỉ-icon (bút / khóa / thùng rác) theo thiết kế.
  const iconBtn = (ico, onClick, title, danger) => (
    <button className="iconbtn" onClick={onClick} title={title} style={danger ? { color: 'var(--danger)' } : undefined}>
      <i className={`ti ${ico}`} aria-hidden="true" />
    </button>
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Quản lý nhân viên</h2>
          <div className="sub">Tạo tài khoản & phân cấp: Giám đốc → Trưởng phòng → Nhân viên</div>
        </div>
        <div className="row">
          <button className="btn ghost" onClick={openManageGroups}>
            Nhóm kinh doanh
          </button>
          <button className="btn ghost" onClick={openDeptHeads}>
            Trưởng phòng ban
          </button>
          <button className="btn ghost" onClick={openDeputyHeads}>
            Phó phòng kinh doanh
          </button>
          <button className="btn ghost" onClick={openCreatePartner}>
            + Tài khoản đối tác
          </button>
          <button className="btn" onClick={openCreateUser}>
            + Thêm tài khoản
          </button>
        </div>
      </div>
      <div className="card">
        <div className="row between" style={{ marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
          <div className="row" style={{ gap: 24, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span className="small muted"><b className="mono" style={{ fontSize: 17, color: 'var(--text)' }}>{visibleUsers.length}</b> tài khoản</span>
            {counts.map((g) => (
              <span key={g.key} className="small muted">
                <b className="mono" style={{ fontSize: 17, color: g.dot }}>{g.n}</b> {g.label}
              </span>
            ))}
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {resignedCount > 0 && (
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => setHideResigned((v) => !v)}
                style={hideResigned ? { color: 'var(--accent)', borderColor: 'var(--accent)' } : undefined}
                title={hideResigned ? 'Đang ẩn nhân viên đã nghỉ — bấm để hiện lại' : 'Ẩn nhân viên đã nghỉ khỏi danh sách'}
              >
                {hideResigned ? `👁 Hiện đã nghỉ (${resignedCount})` : `🚫 Ẩn đã nghỉ (${resignedCount})`}
              </button>
            )}
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="🔍 Tìm tên, mã NV, email…"
              style={{ maxWidth: 280, width: '100%' }}
            />
          </div>
        </div>
        <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>Mã NV</th>
              <th>Nhân sự</th>
              <th>Nhóm</th>
              <th>Liên hệ</th>
              <th className="r">Lương CB</th>
              <th>Quản lý</th>
              <th className="c">Đồng bộ</th>
              <th className="c">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {rankGroups.map((grp) => (
              <Fragment key={grp.key}>
                <tr className="kpi-group">
                  <td colSpan={8}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: grp.dot, marginRight: 8 }} />
                    {grp.label} <span className="small muted">({grp.rows.length})</span>
                  </td>
                </tr>
                {grp.rows.map((u) => (
              u.role === 'DT' ? (
              <tr key={u.id} style={u.resignedAt ? { opacity: 0.55 } : undefined}>
                <td><span className="tag" style={{ background: 'color-mix(in srgb, var(--accent2) 16%, transparent)', color: 'var(--accent2)', fontSize: 11 }}>Đối tác</span></td>
                <td colSpan={6}>
                  <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                    <Avatar src={u.avatar} name={u.name} size={38} />
                    <div style={{ lineHeight: 1.35 }}>
                      <div style={{ fontWeight: 600 }}>
                        {u.name}
                        {u.resignedAt && <span className="tag" style={{ marginLeft: 8, background: 'color-mix(in srgb, var(--danger) 15%, transparent)', color: 'var(--danger)', fontSize: 11 }}>⛔ Đã khoá</span>}
                      </div>
                      <code className="small" style={{ color: 'var(--accent2)' }}>@{u.user}</code>
                      <span className="small muted" style={{ marginLeft: 10, cursor: 'pointer' }} onClick={() => openPartnerContacts(u)} title="Bấm để sửa danh sách được chat">
                        💬 được chat với {(u.partnerContacts || []).length} người
                      </span>
                    </div>
                  </div>
                </td>
                <td className="c">
                  <div className="row" style={{ gap: 4, justifyContent: 'center' }}>
                    {iconBtn('ti-users', () => openPartnerContacts(u), 'Sửa danh sách được chat')}
                    {iconBtn('ti-key', () => doResetPassword(u), 'Reset mật khẩu')}
                    {iconBtn(u.resignedAt ? 'ti-user-check' : 'ti-logout', () => doToggleResigned(u), u.resignedAt ? 'Mở lại tài khoản' : 'Khoá đăng nhập', !u.resignedAt)}
                    {iconBtn('ti-trash', () => doRemoveUser(u), 'Xóa tài khoản đối tác', true)}
                  </div>
                </td>
              </tr>
              ) : (
              <tr key={u.id} style={u.resignedAt ? { opacity: 0.55 } : undefined}>
                <td>
                  {u.employeeCode ? (
                    <code style={{ color: 'var(--warn)' }}>{u.employeeCode}</code>
                  ) : (
                    <span className="small muted">—</span>
                  )}
                  {(u.pmboxCodes || []).length > 0 && (
                    <div className="small muted" style={{ marginTop: 3 }} title="Mã bên pmbox">
                      pmbox: <code style={{ color: 'var(--accent2)' }}>{u.pmboxCodes.join(', ')}</code>
                    </div>
                  )}
                </td>
                <td>
                  <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                    <span style={{ cursor: 'pointer' }} onClick={() => openEditContact(u)} title="Bấm để đổi ảnh">
                      <Avatar src={u.avatar} name={u.name} size={38} />
                    </span>
                    <div style={{ lineHeight: 1.35 }}>
                      <div style={{ fontWeight: 600 }}>
                        {u.name}
                        {u.resignedAt && (
                          <span className="tag" title={`Nghỉ từ ${new Date(u.resignedAt).toLocaleDateString('vi-VN')} — không đăng nhập được`}
                            style={{ marginLeft: 8, background: 'color-mix(in srgb, var(--danger) 15%, transparent)', color: 'var(--danger)', fontSize: 11 }}>
                            ⛔ Đã nghỉ {new Date(u.resignedAt).toLocaleDateString('vi-VN')}
                          </span>
                        )}
                        {ctvDepts(u.departments).map((d) => (
                          <span key={d} className="tag" style={{ marginLeft: 8, background: 'color-mix(in srgb, var(--accent) 16%, transparent)', color: 'var(--accent)', fontSize: 11 }}>CTV {deptShort(d)}</span>
                        ))}
                        {u.isDriver && (
                          <span className="tag" title="Lái xe: chấm 1 lần/ngày là đủ công" style={{ marginLeft: 8, background: 'color-mix(in srgb, var(--warn) 16%, transparent)', color: 'var(--warn)', fontSize: 11 }}>🚚 Lái xe</span>
                        )}
                        {u.isDeputy && (
                          <span className="tag" title="Phó phòng kinh doanh — quản lý một tập con nhân viên" style={{ marginLeft: 8, background: 'color-mix(in srgb, var(--warn) 20%, transparent)', color: 'var(--warn)', fontSize: 11 }}>⭐ Phó phòng</span>
                        )}
                      </div>
                      <code className="small" style={{ color: 'var(--accent2)' }}>@{u.user}</code>
                      {(u.role === 'NV' || u.role === 'TP') && (
                        <span className="tag" title="Ngày phép — bấm xem lịch sử" style={{ marginLeft: 8, cursor: 'pointer', background: 'color-mix(in srgb, var(--ok) 15%, transparent)', color: 'var(--ok)', fontSize: 11 }}
                          onClick={() => openModal(<LeaveHistoryModal user={u} isGD={me.role === 'GD' || me.role === 'CV' || (me.deptHeadOf || []).includes('Phòng kế toán')} onClose={closeModal} onChanged={reload} />)}>
                          🏖 {u.leaveBalance ?? 0} phép
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td>{u.group ? <span className="tag">{u.group.name}</span> : <span className="small muted">—</span>}</td>
                <td onClick={() => openEditContact(u)} style={{ cursor: 'pointer' }} title="Bấm để sửa thông tin liên hệ">
                  {u.email || u.phone ? (
                    <div style={{ lineHeight: 1.35 }}>
                      <div>{u.email || <span className="small muted">—</span>}</div>
                      {u.phone && <div className="small muted">{u.phone}</div>}
                    </div>
                  ) : (
                    <span className="small muted">—</span>
                  )}
                </td>
                <td
                  className="r"
                  onClick={() => !isAdminRole(u.role) && openEditSalary(u)}
                  style={!isAdminRole(u.role) ? { cursor: 'pointer' } : {}}
                  title={!isAdminRole(u.role) ? 'Bấm để sửa lương' : ''}
                >
                  {u.baseSalary ? (
                    <div style={{ lineHeight: 1.35 }}>
                      <div style={{ fontWeight: 600 }}>{fmtVND(u.baseSalary)}</div>
                      {u.allowance ? <div className="small" style={{ color: 'var(--ok)' }}>+ {fmtVND(u.allowance)}</div> : null}
                      <div className="small" style={{ color: u.payInsurance !== false ? 'var(--accent)' : 'var(--muted)' }}>
                        {u.payInsurance !== false ? '● Có BHXH' : '○ Không BHXH'}
                      </div>
                    </div>
                  ) : (
                    <span className="small muted">—</span>
                  )}
                </td>
                <td>{u.manager ? u.manager.name : <span className="small muted">—</span>}</td>
                <td className="c">
                  {isAdminRole(u.role) ? (
                    <span className="chip done" title="Admin luôn được chạy đồng bộ">Luôn bật</span>
                  ) : (
                    <button
                      className={`filterBtn${u.syncAgent ? ' on' : ''}`}
                      onClick={() => toggleAgent(u)}
                      title="Cho phép máy/tài khoản này chạy đồng bộ tự động (failover)"
                    >
                      {u.syncAgent ? 'Bật' : 'Tắt'}
                    </button>
                  )}
                </td>
                <td className="c">
                  <div className="row" style={{ gap: 4, justifyContent: 'center' }}>
                    {iconBtn('ti-pencil', () => openEditUser(u), 'Sửa toàn bộ thông tin nhân viên')}
                    {u.id !== me.id ? (
                      <>
                        {/* Reset được cả Cố vấn (CV) — chỉ giấu với tài khoản Giám đốc (GD, backend cũng chặn). */}
                        {u.role !== 'GD' && iconBtn('ti-key', () => doResetPassword(u), 'Reset mật khẩu')}
                        {iconBtn(u.resignedAt ? 'ti-user-check' : 'ti-logout', () => doToggleResigned(u),
                          u.resignedAt ? 'Cho làm việc trở lại' : 'Đánh dấu ĐÃ NGHỈ việc (chặn đăng nhập, giữ tài khoản)', !u.resignedAt)}
                        {(u.role === 'NV' || u.role === 'TP') && !u.resignedAt
                          && iconBtn('ti-user-share', () => doConvertToPartner(u), 'Chuyển thành tài khoản đối tác')}
                        {iconBtn('ti-trash', () => doRemoveUser(u), 'Xóa tài khoản', true)}
                      </>
                    ) : (
                      <span className="small muted" style={{ marginLeft: 4 }}>Bạn</span>
                    )}
                  </div>
                </td>
              </tr>
              )
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

function EditSalaryForm({ user, onCancel, onSubmit }) {
  const [salary, setSalary] = useState(user.baseSalary || 0);
  const [allowance, setAllowance] = useState(user.allowance || 0);
  const [payInsurance, setPayInsurance] = useState(user.payInsurance !== false);
  const [busy, setBusy] = useState(false);
  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      await onSubmit({ baseSalary: Number(salary) || 0, allowance: Number(allowance) || 0, payInsurance });
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <span className="close" onClick={onCancel}>
        ×
      </span>
      <h3>Lương — {user.name}</h3>
      <div className="two-col">
        <div className="field">
          <label>Lương cơ bản (đồng/tháng)</label>
          <input type="number" value={salary} onChange={(e) => setSalary(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label>Phụ cấp (đồng/tháng)</label>
          <input type="number" value={allowance} onChange={(e) => setAllowance(e.target.value)} />
        </div>
      </div>
      <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={payInsurance} onChange={(e) => setPayInsurance(e.target.checked)} style={{ width: 'auto' }} />
        <span>Đóng bảo hiểm (BHXH) — trừ 10,5% lương NV, công ty đóng 21,5%</span>
      </label>
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onCancel}>
          Hủy
        </button>
        <button className="btn" onClick={save} disabled={busy}>
          {busy ? 'Đang lưu…' : 'Lưu'}
        </button>
      </div>
    </>
  );
}

function EditContactForm({ user, onCancel, onSubmit }) {
  const [email, setEmail] = useState(user.email || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [dob, setDob] = useState(user.dob ? new Date(user.dob).toISOString().slice(0, 10) : '');
  const [avatar, setAvatar] = useState(user.avatar || '');
  const [busy, setBusy] = useState(false);
  async function save() {
    if (busy) return;
    if (phone && !isValidPhone(phone)) return; // chặn ở UI; thông báo qua placeholder/hint
    setBusy(true);
    try {
      await onSubmit({ email: email.trim(), phone: phone.trim(), dob: dob || null, avatar });
    } finally {
      setBusy(false);
    }
  }
  const phoneBad = phone && !isValidPhone(phone);
  return (
    <>
      <span className="close" onClick={onCancel}>
        ×
      </span>
      <h3>Thông tin liên hệ — {user.name}</h3>
      <div className="field">
        <label>Ảnh đại diện</label>
        <AvatarPicker name={user.name} value={avatar} onChange={setAvatar} />
      </div>
      <div className="field">
        <label>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vd: nhanvien@congty.com" />
      </div>
      <div className="two-col">
        <div className="field">
          <label>Số điện thoại (10 chữ số)</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="vd: 0901234567"
            inputMode="numeric"
          />
          {phoneBad && <span className="small" style={{ color: 'var(--danger)' }}>SĐT phải gồm đúng 10 chữ số</span>}
        </div>
        <div className="field">
          <label>Ngày sinh</label>
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
        </div>
      </div>
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onCancel}>
          Hủy
        </button>
        <button className="btn" onClick={save} disabled={busy || phoneBad}>
          {busy ? 'Đang lưu…' : 'Lưu'}
        </button>
      </div>
    </>
  );
}

// Sửa TOÀN BỘ thông tin nhân viên (GĐ). Không đổi mật khẩu ở đây. Đang là TP thì giữ vai trò TP.
function EditUserForm({ user, groups, onCancel, onSubmit }) {
  const isTP = user.role === 'TP';
  const [name, setName] = useState(user.name || '');
  const [employeeCode, setEmployeeCode] = useState(user.employeeCode || '');
  const [pmboxCodes, setPmboxCodes] = useState((user.pmboxCodes || []).join(', ')); // 1 NV gộp nhiều mã pmbox
  const [login, setLogin] = useState(user.user || '');
  const [email, setEmail] = useState(user.email || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [dob, setDob] = useState(user.dob ? new Date(user.dob).toISOString().slice(0, 10) : '');
  const [avatar, setAvatar] = useState(user.avatar || '');
  const [role, setRole] = useState(user.role);
  const [group, setGroup] = useState(user.group ? String(user.group.id ?? user.group._id ?? user.group) : '');
  const [departments, setDepartments] = useState(user.departments || []);
  const [isDriver, setIsDriver] = useState(!!user.isDriver); // LÁI XE: chấm 1 lần/ngày = đủ công
  const [custChatAll, setCustChatAll] = useState(!!user.custChatAll); // chăm MỌI luồng chat khách
  const [baseSalary, setBaseSalary] = useState(user.baseSalary || 0);
  const [allowance, setAllowance] = useState(user.allowance || 0);
  const [busy, setBusy] = useState(false);

  function toggleDept(d) {
    setDepartments((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }
  // Phòng CHÍNH = phần tử đầu mảng: quyết định NV thuộc tab nào ở bảng lương/chấm công.
  function setPrimaryDept(d) {
    setDepartments((prev) => (prev.includes(d) ? [d, ...prev.filter((x) => x !== d)] : [d, ...prev]));
  }
  const phoneBad = phone && !isValidPhone(phone);
  const showGroup = role === 'NV'; // Admin không thuộc nhóm; TP đổi nhóm qua Quản lý nhóm.

  async function save() {
    if (busy || phoneBad) return;
    if (!name.trim() || !login.trim()) return;
    setBusy(true);
    try {
      await onSubmit({
        name: name.trim(),
        employeeCode: employeeCode.trim(),
        pmboxCodes, // chuỗi "a, b c" -> backend tách/khử trùng
        user: login.trim(),
        role: isTP ? undefined : role, // TP: không đổi
        group: showGroup ? group : '',
        departments,
        isDriver: role === 'NV' && departments.includes('Phòng OP') ? isDriver : false,
        custChatAll,
        baseSalary: Number(baseSalary) || 0,
        allowance: Number(allowance) || 0,
        email: email.trim(),
        phone: phone.trim(),
        dob: dob || null,
        avatar,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span className="close" onClick={onCancel}>
        ×
      </span>
      <h3>Sửa thông tin — {user.name}</h3>
      <div className="field">
        <label>Ảnh đại diện</label>
        <AvatarPicker name={name} value={avatar} onChange={setAvatar} />
      </div>
      <div className="two-col">
        <div className="field">
          <label>Họ tên (khớp tên sale trên SocSon)</label>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label>Mã nhân viên (khớp mã bên SocSon)</label>
          <input value={employeeCode} onChange={(e) => setEmployeeCode(e.target.value)} autoComplete="off" />
        </div>
      </div>
      <div className="field">
        <label>Mã pmbox (nhiều mã cách nhau dấu phẩy)</label>
        <input value={pmboxCodes} onChange={(e) => setPmboxCodes(e.target.value)} placeholder="vd: PB012, PB088" autoComplete="off" />
        <div className="small muted" style={{ marginTop: 4 }}>1 nhân viên có thể gộp 2–3 tài khoản pmbox. Để trống nếu không có.</div>
      </div>
      <div className="two-col">
        <div className="field">
          <label>Tên đăng nhập</label>
          <input value={login} onChange={(e) => setLogin(e.target.value)} autoComplete="off" />
        </div>
        <div className="field">
          <label>Cấp bậc</label>
          {isTP ? (
            <>
              <input value="Trưởng phòng" disabled />
              <div className="small muted" style={{ marginTop: 6 }}>
                Đổi/bỏ trưởng phòng ở mục "Nhóm kinh doanh".
              </div>
            </>
          ) : (
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="NV">Nhân viên</option>
              <option value="GD">Giám đốc (toàn quyền)</option>
              <option value="CV">Cố vấn (toàn quyền)</option>
            </select>
          )}
        </div>
      </div>
      <div className="two-col">
        <div className="field">
          <label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vd: nhanvien@congty.com" autoComplete="off" />
        </div>
        <div className="field">
          <label>Số điện thoại (10 chữ số)</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="vd: 0901234567"
            inputMode="numeric"
            autoComplete="off"
          />
          {phoneBad && <span className="small" style={{ color: 'var(--danger)' }}>SĐT phải gồm đúng 10 chữ số</span>}
        </div>
      </div>
      <div className="field">
        <label>Ngày sinh</label>
        <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
      </div>
      {showGroup && (
        <div className="field">
          <label>Nhóm kinh doanh (không bắt buộc)</label>
          <select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="">— Không thuộc nhóm kinh doanh —</option>
            {groups.map((g) => (
              <option key={g._id} value={g._id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="field">
        <label>Phòng ban kiêm nhiệm (chọn 1 hoặc nhiều)</label>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {DEPARTMENTS.map((d) => (
            <button
              key={d}
              type="button"
              className={`filterBtn ${departments.includes(d) ? 'on' : ''}`}
              onClick={() => toggleDept(d)}
            >
              {departments[0] === d ? '★ ' : departments.includes(d) ? '✓ ' : ''}
              {d}
            </button>
          ))}
        </div>
        {departments.length > 1 && (
          <div style={{ marginTop: 8 }}>
            <div className="small muted" style={{ marginBottom: 4 }}>Phòng chính (★) — nơi làm việc chính (lương & chấm công tính ở đây). Các phòng còn lại: NV là <b>CTV</b> của phòng đó.</div>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {departments.map((d) => (
                <button key={d} type="button" className={`filterBtn ${departments[0] === d ? 'on' : ''}`} onClick={() => setPrimaryDept(d)}>
                  {departments[0] === d ? '★ ' : ''}{d}
                </button>
              ))}
            </div>
          </div>
        )}
        {role === 'NV' && departments.includes('Phòng OP') && (
          <label className="row" style={{ gap: 8, marginTop: 10, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={isDriver} onChange={(e) => setIsDriver(e.target.checked)} />
            <span>🚚 <b>Lái xe</b> (Phòng OP) — chỉ cần chấm công <b>1 lần/ngày</b> là đủ công, không tính đi muộn/về sớm.</span>
          </label>
        )}
        {role !== 'GD' && role !== 'CV' && (
          <label className="row" style={{ gap: 8, marginTop: 10, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={custChatAll} onChange={(e) => setCustChatAll(e.target.checked)} />
            <span>💬 <b>Chăm mọi luồng chat khách</b> — nhận thông báo + xem <b>tất cả luồng</b> (Kinh doanh / CSKH / Phòng mua) của <b>mọi khách</b>, kể cả khách không phải mình phụ trách.</span>
          </label>
        )}
      </div>
      {!isAdminRole(role) && (
        <div className="two-col">
          <div className="field">
            <label>Lương cơ bản (đồng/tháng)</label>
            <input type="number" value={baseSalary} onChange={(e) => setBaseSalary(e.target.value)} />
          </div>
          <div className="field">
            <label>Phụ cấp (đồng/tháng)</label>
            <input type="number" value={allowance} onChange={(e) => setAllowance(e.target.value)} />
          </div>
        </div>
      )}
      <div className="small muted" style={{ marginTop: 8 }}>
        Mật khẩu đổi bằng nút "🔑 Reset MK" riêng — không sửa ở đây.
      </div>
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onCancel}>
          Hủy
        </button>
        <button className="btn" onClick={save} disabled={busy || phoneBad || !name.trim() || !login.trim()}>
          {busy ? 'Đang lưu…' : 'Lưu'}
        </button>
      </div>
    </>
  );
}

// Form TÀI KHOẢN ĐỐI TÁC (role=DT). editUser != null -> chỉ sửa danh sách "được chat với".
// pool = nhân sự có thể được chọn (không gồm đối tác khác). Ưu tiên xếp Phòng DVKH lên đầu.
function PartnerForm({ pool, editUser, onCancel, onSubmit, title, subtitle, okText }) {
  const editing = !!editUser;
  const [name, setName] = useState(editUser?.name || '');
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [q, setQ] = useState('');
  const [contacts, setContacts] = useState(() => new Set((editUser?.partnerContacts || []).map(String)));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setContacts((prev) => { const n = new Set(prev); const k = String(id); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const isDVKH = (u) => (u.departments || []).includes('Phòng DVKH');
  const ql = q.trim().toLowerCase();
  const filtered = pool.filter((u) => !ql || [u.name, u.user].some((v) => String(v || '').toLowerCase().includes(ql)));
  // DVKH lên đầu, rồi theo tên.
  const sorted = [...filtered].sort((a, b) => (isDVKH(b) - isDVKH(a)) || String(a.name).localeCompare(String(b.name)));

  const valid = editing ? true : (name.trim() && user.trim() && pass.trim());
  async function submit() {
    if (busy || !valid) return;
    setBusy(true);
    try {
      await onSubmit(editing
        ? { contacts: [...contacts] }
        : { name: name.trim(), user: user.trim(), pass, contacts: [...contacts] });
    } finally { setBusy(false); }
  }

  return (
    <>
      <span className="close" onClick={onCancel}>×</span>
      <h3>{title || (editing ? `Sửa danh sách chat — ${editUser.name}` : 'Tạo tài khoản đối tác')}</h3>
      <div className="small muted" style={{ marginBottom: 12 }}>{subtitle || '🔒 Đối tác đăng nhập chỉ thấy Tin nhắn — không xem được phần nào khác. Chỉ chat được với người bạn chọn dưới đây (ẩn hai chiều với người còn lại).'}</div>
      {!editing && (
        <>
          <div className="field">
            <label>Tên đối tác</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="vd: Xưởng in Việt Anh" />
          </div>
          <div className="two-col">
            <div className="field">
              <label>Tên đăng nhập</label>
              <input value={user} onChange={(e) => setUser(e.target.value)} placeholder="doitac_vietanh" autoComplete="off" />
            </div>
            <div className="field">
              <label>Mật khẩu</label>
              <input value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Đặt mật khẩu" autoComplete="new-password" />
            </div>
          </div>
        </>
      )}
      <div className="field">
        <label>Được chat với ai <span className="small muted">(đã chọn {contacts.size})</span></label>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Tìm nhân viên…" style={{ marginBottom: 6 }} />
        <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
          {sorted.length === 0 && <div className="small muted" style={{ padding: 10 }}>Không có nhân viên phù hợp.</div>}
          {sorted.map((u) => (
            <label key={u.id} className="row" style={{ gap: 10, alignItems: 'center', padding: '7px 12px', cursor: 'pointer', fontWeight: 400, borderTop: '1px solid var(--line)' }}>
              <input type="checkbox" checked={contacts.has(String(u.id))} onChange={() => toggle(u.id)} style={{ width: 'auto' }} />
              <span>{u.name}</span>
              {isDVKH(u) && <span className="small" style={{ color: 'var(--accent)' }}>· DVKH</span>}
              <code className="small muted" style={{ marginLeft: 'auto' }}>@{u.user}</code>
            </label>
          ))}
        </div>
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
        <button className="btn ghost" onClick={onCancel}>Hủy</button>
        <button className="btn" disabled={busy || !valid} onClick={submit}>{busy ? 'Đang lưu…' : (okText || (editing ? 'Lưu' : 'Tạo tài khoản'))}</button>
      </div>
    </>
  );
}

function CreateUserForm({ groups, onCancel, onSubmit }) {
  const [name, setName] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [pmboxCodes, setPmboxCodes] = useState(''); // 1 NV gộp nhiều mã pmbox (cách nhau dấu phẩy)
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [avatar, setAvatar] = useState('');
  const [role, setRole] = useState('NV');
  const [group, setGroup] = useState('');
  const [departments, setDepartments] = useState([]);
  const [isDriver, setIsDriver] = useState(false); // LÁI XE: chấm 1 lần/ngày = đủ công
  const [baseSalary, setBaseSalary] = useState('');
  const [allowance, setAllowance] = useState('');
  const [busy, setBusy] = useState(false);

  function toggleDept(d) {
    setDepartments((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }
  // Phòng CHÍNH = phần tử đầu mảng: quyết định NV thuộc tab nào ở bảng lương/chấm công.
  function setPrimaryDept(d) {
    setDepartments((prev) => (prev.includes(d) ? [d, ...prev.filter((x) => x !== d)] : [d, ...prev]));
  }

  const phoneBad = phone && !isValidPhone(phone);

  async function submit() {
    if (busy || phoneBad) return;
    setBusy(true);
    try {
      await onSubmit({
        name,
        employeeCode,
        pmboxCodes,
        user,
        pass,
        role,
        group: role === 'NV' ? group : '',
        departments,
        isDriver: role === 'NV' && departments.includes('Phòng OP') ? isDriver : false,
        baseSalary: Number(baseSalary) || 0,
        allowance: Number(allowance) || 0,
        email: email.trim(),
        phone: phone.trim(),
        dob: dob || null,
        avatar,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span className="close" onClick={onCancel}>
        ×
      </span>
      <h3>Thêm tài khoản nhân viên</h3>
      <div className="field">
        <label>Ảnh đại diện</label>
        <AvatarPicker name={name} value={avatar} onChange={setAvatar} />
      </div>
      <div className="two-col">
        <div className="field">
          <label>Họ tên (khớp tên sale trên SocSon)</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nguyễn Văn A" />
        </div>
        <div className="field">
          <label>Mã nhân viên (khớp mã bên SocSon)</label>
          <input value={employeeCode} onChange={(e) => setEmployeeCode(e.target.value)} placeholder="vd: NV007" autoComplete="off" />
        </div>
      </div>
      <div className="field">
        <label>Mã pmbox (nhiều mã cách nhau dấu phẩy)</label>
        <input value={pmboxCodes} onChange={(e) => setPmboxCodes(e.target.value)} placeholder="vd: PB012, PB088" autoComplete="off" />
        <div className="small muted" style={{ marginTop: 4 }}>1 nhân viên có thể gộp 2–3 tài khoản pmbox. Để trống nếu không có.</div>
      </div>
      <div className="two-col">
        <div className="field">
          <label>Tên đăng nhập</label>
          <input value={user} onChange={(e) => setUser(e.target.value)} placeholder="vd: nhanvienA" autoComplete="off" />
        </div>
        <div className="field">
          <label>Mật khẩu tạm</label>
          <input value={pass} onChange={(e) => setPass(e.target.value)} placeholder="đặt mật khẩu" />
        </div>
      </div>
      <div className="two-col">
        <div className="field">
          <label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vd: nhanvien@congty.com" autoComplete="off" />
        </div>
        <div className="field">
          <label>Số điện thoại (10 chữ số)</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="vd: 0901234567"
            inputMode="numeric"
            autoComplete="off"
          />
          {phoneBad && <span className="small" style={{ color: 'var(--danger)' }}>SĐT phải gồm đúng 10 chữ số</span>}
        </div>
      </div>
      <div className="field">
        <label>Ngày sinh</label>
        <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
      </div>
      <div className="field">
        <label>Cấp bậc</label>
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="NV">Nhân viên</option>
          <option value="GD">Giám đốc (toàn quyền)</option>
          <option value="CV">Cố vấn (toàn quyền)</option>
        </select>
        {(role === 'GD' || role === 'CV') && (
          <div className="small muted" style={{ marginTop: 6 }}>
            Giám đốc & Cố vấn có toàn quyền như nhau; không thuộc nhóm kinh doanh.
          </div>
        )}
      </div>
      {role === 'NV' && (
        <div className="field">
          <label>Nhóm kinh doanh (không bắt buộc)</label>
          <select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="">— Không thuộc nhóm kinh doanh —</option>
            {groups.map((g) => (
              <option key={g._id} value={g._id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="field">
        <label>Phòng ban kiêm nhiệm (chọn 1 hoặc nhiều)</label>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {DEPARTMENTS.map((d) => (
            <button
              key={d}
              type="button"
              className={`filterBtn ${departments.includes(d) ? 'on' : ''}`}
              onClick={() => toggleDept(d)}
            >
              {departments[0] === d ? '★ ' : departments.includes(d) ? '✓ ' : ''}
              {d}
            </button>
          ))}
        </div>
        {departments.length > 1 && (
          <div style={{ marginTop: 8 }}>
            <div className="small muted" style={{ marginBottom: 4 }}>Phòng chính (★) — nơi làm việc chính (lương & chấm công tính ở đây). Các phòng còn lại: NV là <b>CTV</b> của phòng đó.</div>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {departments.map((d) => (
                <button key={d} type="button" className={`filterBtn ${departments[0] === d ? 'on' : ''}`} onClick={() => setPrimaryDept(d)}>
                  {departments[0] === d ? '★ ' : ''}{d}
                </button>
              ))}
            </div>
          </div>
        )}
        {role === 'NV' && departments.includes('Phòng OP') && (
          <label className="row" style={{ gap: 8, marginTop: 10, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={isDriver} onChange={(e) => setIsDriver(e.target.checked)} />
            <span>🚚 <b>Lái xe</b> (Phòng OP) — chỉ cần chấm công <b>1 lần/ngày</b> là đủ công, không tính đi muộn/về sớm.</span>
          </label>
        )}
      </div>
      <div className="two-col">
        <div className="field">
          <label>Lương cơ bản (đồng/tháng)</label>
          <input type="number" value={baseSalary} onChange={(e) => setBaseSalary(e.target.value)} placeholder="vd: 8000000" />
        </div>
        <div className="field">
          <label>Phụ cấp (đồng/tháng)</label>
          <input type="number" value={allowance} onChange={(e) => setAllowance(e.target.value)} placeholder="vd: 1000000" />
        </div>
      </div>
      <div className="demo-hint">
        Nhân viên mới được thêm vào nhóm kinh doanh đã chọn. Việc bổ nhiệm <b>trưởng phòng</b> được thực hiện ở mục
        "Nhóm kinh doanh" — mỗi nhóm chọn 1 trưởng phòng trong số nhân viên của nhóm. Mã nhân viên dùng để khớp dữ liệu
        doanh thu đồng bộ từ SocSon.
      </div>
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onCancel}>
          Hủy
        </button>
        <button className="btn" onClick={submit} disabled={busy || phoneBad}>
          {busy ? 'Đang tạo…' : 'Tạo tài khoản'}
        </button>
      </div>
    </>
  );
}

function GroupsModal({ onClose, onChanged, toast }) {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false); // chống gửi trùng (double-submit)

  // Modal tự tải dữ liệu để luôn hiển thị trạng thái mới nhất sau khi thêm/sửa/xóa.
  async function reloadLocal() {
    setLoading(true);
    try {
      const [g, u] = await Promise.all([listGroups(), listUsers()]);
      setGroups(g);
      setUsers(u);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reloadLocal();
  }, []);

  // Cập nhật lại cả dữ liệu trong modal lẫn ở trang cha (bảng tài khoản).
  async function refreshAll() {
    await reloadLocal();
    onChanged();
  }

  async function add() {
    if (busy) return; // đang xử lý request trước đó -> bỏ qua click/Enter lặp
    if (!name.trim()) return toast('Nhập tên nhóm', 'warn');
    setBusy(true);
    try {
      await createGroup(name.trim());
      const added = name.trim();
      setName('');
      toast('Đã thêm ' + added);
      await refreshAll();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBusy(false);
    }
  }

  function startEdit(g) {
    setEditingId(g._id);
    setEditName(g.name);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
  }

  async function saveEdit(id) {
    if (busy) return;
    if (!editName.trim()) return toast('Nhập tên nhóm', 'warn');
    setBusy(true);
    try {
      await updateGroup(id, editName.trim());
      toast('Đã đổi tên nhóm');
      cancelEdit();
      await refreshAll();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    if (busy) return;
    setBusy(true);
    try {
      await removeGroup(id);
      toast('Đã xóa nhóm', 'warn');
      await refreshAll();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBusy(false);
    }
  }

  // Bổ nhiệm trưởng phòng cho nhóm (userId rỗng = bỏ trưởng phòng).
  async function chooseHead(groupId, userId) {
    if (busy) return;
    setBusy(true);
    try {
      await setGroupHead(groupId, userId || null);
      toast(userId ? 'Đã đặt trưởng nhóm' : 'Đã bỏ trưởng nhóm');
      await refreshAll();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span className="close" onClick={onClose}>
        ×
      </span>
      <h3>Nhóm kinh doanh</h3>
      <div className="tbl-wrap">
      <table style={{ marginBottom: 14 }}>
        <thead>
          <tr>
            <th>Tên nhóm</th>
            <th>Trưởng phòng (chọn từ thành viên)</th>
            <th className="c">Số TV</th>
            <th className="c">Thao tác</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={4} className="empty">
                Đang tải...
              </td>
            </tr>
          ) : groups.length ? (
            groups.map((g) => {
              const members = users.filter((u) => String(u.group?.id) === String(g._id));
              const currentTP = members.find((u) => u.role === 'TP');
              // Trưởng phòng KINH DOANH "mồ côi" (role TP, mất nhóm, vẫn thuộc Phòng kinh doanh)
              // -> cho chọn lại để khôi phục. Loại TP phòng ban khác (DVKH/CSKH… vốn không có nhóm).
              const orphanTPs = users.filter((u) => u.role === 'TP' && !u.group && (u.departments || []).includes('Phòng kinh doanh'));
              const candidates = [...members, ...orphanTPs.filter((o) => !members.some((m) => m.id === o.id))];
              const isEditing = editingId === g._id;
              return (
                <tr key={g._id}>
                  <td>
                    {isEditing ? (
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit(g._id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                        style={{ padding: '6px 8px' }}
                      />
                    ) : (
                      g.name
                    )}
                  </td>
                  <td>
                    {candidates.length === 0 ? (
                      <span className="small muted">Nhóm chưa có nhân viên</span>
                    ) : (
                      <select
                        value={currentTP?.id || ''}
                        onChange={(e) => chooseHead(g._id, e.target.value)}
                        disabled={busy}
                        style={{ padding: '6px 8px', width: 'auto', minWidth: 160 }}
                      >
                        <option value="">— Chưa chọn —</option>
                        {candidates.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                            {m.employeeCode ? ` (${m.employeeCode})` : ''}
                            {!m.group ? ' · chưa có nhóm' : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="c">{members.length}</td>
                  <td className="c">
                    {isEditing ? (
                      <div className="row" style={{ gap: 6, justifyContent: 'center' }}>
                        <button className="btn sm ok" onClick={() => saveEdit(g._id)}>
                          Lưu
                        </button>
                        <button className="btn sm ghost" onClick={cancelEdit}>
                          Hủy
                        </button>
                      </div>
                    ) : (
                      <div className="row" style={{ gap: 6, justifyContent: 'center' }}>
                        <button className="btn sm ghost" onClick={() => startEdit(g)}>
                          Sửa
                        </button>
                        <button className="btn sm ghost" onClick={() => remove(g._id)}>
                          Xóa
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={4} className="empty">
                Chưa có nhóm
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      <div className="field">
        <label>Thêm nhóm mới</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="vd: Nhóm Kinh doanh 3"
        />
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onClose}>
          Đóng
        </button>
        <button className="btn" onClick={add} disabled={busy}>
          {busy ? 'Đang lưu…' : 'Thêm nhóm'}
        </button>
      </div>
    </>
  );
}

// Đặt trưởng phòng cho các phòng ban (ngoài Phòng kinh doanh).
function DeptHeadsModal({ onClose, toast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');

  function reload() {
    setLoading(true);
    listDeptHeads()
      .then(setRows)
      .finally(() => setLoading(false));
  }
  useEffect(reload, []);

  async function save(dep, userId) {
    setSaving(dep);
    try {
      await setDeptHead(dep, userId || '');
      toast('Đã cập nhật trưởng phòng');
      setRows((prev) => prev.map((r) => (r.department === dep ? { ...r, head: userId ? { id: userId, name: r.members.find((m) => String(m.id) === String(userId))?.name } : null } : r)));
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi', 'danger');
    } finally {
      setSaving('');
    }
  }

  return (
    <>
      <span className="close" onClick={onClose}>×</span>
      <h3>Trưởng phòng ban</h3>
      <div className="small muted" style={{ marginBottom: 12 }}>
        Đặt trưởng phòng cho các phòng ban ngoài Phòng kinh doanh (PKD dùng "Nhóm kinh doanh").
      </div>
      {loading ? (
        <div className="empty">Đang tải…</div>
      ) : (
        rows.map((r) => (
          <div className="field" key={r.department}>
            <label>
              {r.department} {r.head ? <span style={{ color: 'var(--accent2)' }}>· TP: {r.head.name}</span> : ''}
            </label>
            <select value={r.head?.id || ''} disabled={saving === r.department} onChange={(e) => save(r.department, e.target.value)}>
              <option value="">— Chưa có trưởng phòng —</option>
              {r.members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            {!r.members.length && <span className="small muted">Chưa có nhân viên nào thuộc phòng ban này.</span>}
          </div>
        ))
      )}
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer" />
        <button className="btn" onClick={onClose}>Xong</button>
      </div>
    </>
  );
}

// Đặt/bỏ PHÓ PHÒNG kinh doanh + chọn tay tập NV do PP quản lý (có thể gồm cả TP: kiêm nhiệm).
function DeputyHeadsModal({ onClose, onChanged, toast }) {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [gid, setGid] = useState('');
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(''); // PP đang mở checklist chọn NV
  const [editSet, setEditSet] = useState(new Set());
  const [kw, setKw] = useState('');
  const [saving, setSaving] = useState('');

  useEffect(() => {
    Promise.all([listUsers(), listGroups()])
      .then(([u, g]) => {
        setUsers(u.filter((x) => x.role !== 'DT' && !x.resignedAt));
        setGroups(g);
        setGid(g[0] ? String(g[0].id || g[0]._id) : '');
      })
      .finally(() => setLoading(false));
  }, []);

  const gidOf = (u) => String(u.group?.id || u.group || '');
  const groupUsers = users.filter((u) => gidOf(u) === String(gid));

  function startEdit(u) {
    setEditId(u.id);
    setEditSet(new Set((u.deputyStaff || []).map(String)));
    setKw('');
  }
  function toggle(id) {
    setEditSet((s) => { const n = new Set(s); n.has(String(id)) ? n.delete(String(id)) : n.add(String(id)); return n; });
  }
  async function saveDeputy(u) {
    setSaving(u.id);
    try {
      const r = await setDeputy(u.id, true, [...editSet]);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, isDeputy: true, deputyStaff: r.deputyStaff || [...editSet] } : x)));
      setEditId('');
      toast('Đã lưu Phó phòng ' + u.name);
      onChanged && onChanged();
    } catch (err) { toast(err.response?.data?.message || 'Có lỗi', 'danger'); }
    finally { setSaving(''); }
  }
  async function clearDeputy(u) {
    setSaving(u.id);
    try {
      await setDeputy(u.id, false, []);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, isDeputy: false, deputyStaff: [] } : x)));
      if (editId === u.id) setEditId('');
      toast('Đã bỏ Phó phòng ' + u.name);
      onChanged && onChanged();
    } catch (err) { toast(err.response?.data?.message || 'Có lỗi', 'danger'); }
    finally { setSaving(''); }
  }
  // Ứng viên 1 PP quản lý: thành viên cùng nhóm (gồm cả TP - kiêm nhiệm), trừ chính PP.
  const candidatesFor = (u) => groupUsers
    .filter((x) => String(x.id) !== String(u.id))
    .filter((x) => !kw.trim() || String(x.name).toLowerCase().includes(kw.trim().toLowerCase()));

  return (
    <>
      <span className="close" onClick={onClose}>×</span>
      <h3>Phó phòng kinh doanh</h3>
      <div className="small muted" style={{ marginBottom: 10 }}>
        Phân cấp: Giám đốc → Trưởng phòng → Phó phòng → Nhân viên. Chọn 1 NV làm Phó phòng rồi tích các nhân viên (có thể gồm cả trưởng phòng — kiêm nhiệm) do PP đó quản lý.
      </div>
      <div className="field">
        <label>Nhóm kinh doanh</label>
        <select value={gid} onChange={(e) => { setGid(e.target.value); setEditId(''); }}>
          {groups.map((g) => <option key={g.id || g._id} value={String(g.id || g._id)}>{g.name}</option>)}
        </select>
      </div>
      {loading ? <div className="empty">Đang tải…</div> : !groupUsers.length ? (
        <div className="empty">Nhóm này chưa có nhân viên.</div>
      ) : (
        <div style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
          {groupUsers.map((u) => {
            const editing = editId === u.id;
            const isTP = u.role === 'TP';
            return (
              <div key={u.id} style={{ borderBottom: '1px solid var(--line)' }}>
                <div className="row" style={{ gap: 8, alignItems: 'center', padding: '8px 10px', background: u.isDeputy ? 'color-mix(in srgb, var(--accent) 10%, transparent)' : undefined }}>
                  <div style={{ flex: 1 }}>
                    <b>{u.name}</b>
                    {isTP && <span className="tag" style={{ marginLeft: 6, fontSize: 11 }}>TP</span>}
                    {u.isDeputy && <span className="tag" style={{ marginLeft: 6, fontSize: 11, background: 'color-mix(in srgb, var(--accent) 18%, transparent)', color: 'var(--accent)' }}>⭐ Phó phòng · {(u.deputyStaff || []).length} người</span>}
                  </div>
                  {isTP ? (
                    <span className="small muted">quản lý cả nhóm</span>
                  ) : editing ? (
                    <button className="btn sm" disabled={saving === u.id} onClick={() => saveDeputy(u)}>{saving === u.id ? 'Đang lưu…' : 'Lưu'}</button>
                  ) : u.isDeputy ? (
                    <>
                      <button className="btn sm ghost" onClick={() => startEdit(u)}>Sửa NV</button>
                      <button className="btn sm" style={{ color: 'var(--danger)' }} disabled={saving === u.id} onClick={() => clearDeputy(u)}>Bỏ</button>
                    </>
                  ) : (
                    <button className="btn sm" onClick={() => startEdit(u)}>Đặt phó phòng</button>
                  )}
                </div>
                {editing && (
                  <div style={{ padding: '8px 10px 12px', background: 'var(--panel2)' }}>
                    <div className="small muted" style={{ marginBottom: 6 }}>Nhân viên do {u.name} quản lý (tích chọn):</div>
                    <input placeholder="Tìm nhân viên…" value={kw} onChange={(e) => setKw(e.target.value)} style={{ marginBottom: 8 }} />
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                      {candidatesFor(u).map((x) => (
                        <label key={x.id} className="row" style={{ gap: 8, alignItems: 'center', padding: '4px 6px', cursor: 'pointer' }}>
                          <input type="checkbox" style={{ width: 'auto' }} checked={editSet.has(String(x.id))} onChange={() => toggle(x.id)} />
                          <span>{x.name}</span>
                          {x.role === 'TP' && <span className="tag" style={{ fontSize: 10 }}>TP · kiêm nhiệm</span>}
                        </label>
                      ))}
                      {!candidatesFor(u).length && <div className="small muted">Không có nhân viên phù hợp.</div>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer" />
        <button className="btn" onClick={onClose}>Xong</button>
      </div>
    </>
  );
}

// Hiện mật khẩu ngẫu nhiên sau khi reset (kèm nút copy). Chỉ xem được 1 lần.
function ResetPasswordResult({ name, login, password, onClose }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard?.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <>
      <span className="close" onClick={onClose}>×</span>
      <h3>🔑 Đã reset mật khẩu</h3>
      <div className="small muted" style={{ marginBottom: 10 }}>
        Mật khẩu mới của <b>{name}</b>
        {login ? <> (đăng nhập: <code>{login}</code>)</> : null}. Gửi cho nhân viên — họ sẽ <b>buộc đổi mật khẩu</b> ở
        lần đăng nhập kế tiếp.
      </div>
      <div className="field">
        <label>Mật khẩu ngẫu nhiên (chỉ hiện 1 lần)</label>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <code style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1, padding: '6px 10px', background: 'var(--panel2)', borderRadius: 8 }}>
            {password}
          </code>
          <button className="btn sm" onClick={copy}>{copied ? '✓ Đã copy' : 'Copy'}</button>
        </div>
      </div>
      <div className="small muted" style={{ marginTop: 8 }}>⚠️ Lưu lại ngay — đóng cửa sổ là không xem lại được.</div>
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer" />
        <button className="btn" onClick={onClose}>Xong</button>
      </div>
    </>
  );
}
